import React from "react";
import { useLanguage } from "@/App";
import { ArrowLeft } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

const PrivacyBeleid = () => {
  const { language } = useLanguage();

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navigation />
      <div className="pt-32 pb-24 max-w-4xl mx-auto px-6 md:px-12">
        <a href="/" className="inline-flex items-center gap-2 text-[#FF6B00] text-sm font-bold mb-8 hover:underline">
          <ArrowLeft size={16} />
          {language === "nl" ? "Terug naar home" : "Back to home"}
        </a>

        <h1 className="text-4xl sm:text-5xl font-black text-white mb-8" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          {language === "nl" ? "Privacybeleid" : "Privacy Policy"}
        </h1>

        <div className="prose prose-invert prose-zinc max-w-none space-y-8 text-zinc-300 leading-relaxed">
          {language === "nl" ? (
            <>
              <p className="text-zinc-400 text-sm">Laatst bijgewerkt: {new Date().toLocaleDateString("nl-NL", { year: "numeric", month: "long", day: "numeric" })}</p>

              <section>
                <h2 className="text-xl font-bold text-white mt-8 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>1. Wie zijn wij?</h2>
                <p>EG Solutions, gevestigd aan Torenallee 101, 5617BR Eindhoven, is verantwoordelijk voor de verwerking van persoonsgegevens zoals weergegeven in dit privacybeleid. Onze directeur, Steven Franken, is de Functionaris Gegevensbescherming.</p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mt-8 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>2. Welke gegevens verzamelen wij?</h2>
                <p>Wij verwerken de volgende persoonsgegevens:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Naam en achternaam</li>
                  <li>E-mailadres</li>
                  <li>Telefoonnummer</li>
                  <li>Bedrijfsnaam</li>
                  <li>Betalingsgegevens (verwerkt via Stripe, wij slaan geen kaartgegevens op)</li>
                  <li>IP-adres en browsergegevens (via cookies)</li>
                  <li>Gegevens die u zelf verstrekt via het contactformulier</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mt-8 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>3. Waarom verzamelen wij deze gegevens?</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Om u te kunnen bellen of e-mailen indien dit nodig is om onze dienstverlening uit te voeren</li>
                  <li>Om offertes en facturen op te stellen</li>
                  <li>Om betalingen te verwerken</li>
                  <li>Om u te informeren over wijzigingen in onze diensten</li>
                  <li>Om onze website te verbeteren (analytics)</li>
                  <li>Om te voldoen aan wettelijke verplichtingen</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mt-8 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>4. Bewaartermijn</h2>
                <p>Wij bewaren uw persoonsgegevens niet langer dan strikt nodig is om de doelen te realiseren waarvoor uw gegevens worden verzameld. Fiscale gegevens worden 7 jaar bewaard conform de wettelijke verplichting.</p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mt-8 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>5. Delen met derden</h2>
                <p>EG Solutions deelt uw persoonsgegevens alleen met derden als dit noodzakelijk is voor de uitvoering van onze overeenkomst of om te voldoen aan een wettelijke verplichting. Wij maken gebruik van:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong className="text-white">Stripe</strong> - voor betalingsverwerking</li>
                  <li><strong className="text-white">Tawk.to</strong> - voor live chat ondersteuning</li>
                  <li><strong className="text-white">PostHog</strong> - voor website analytics</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mt-8 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>6. Uw rechten</h2>
                <p>U heeft het recht om uw persoonsgegevens in te zien, te corrigeren of te verwijderen. U kunt een verzoek indienen via ons contactformulier of WhatsApp (+31 6 84006668). Wij reageren binnen 30 dagen op uw verzoek.</p>
                <p>U heeft tevens het recht om een klacht in te dienen bij de Autoriteit Persoonsgegevens.</p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mt-8 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>7. Beveiliging</h2>
                <p>EG Solutions neemt de bescherming van uw gegevens serieus en neemt passende maatregelen om misbruik, verlies en onbevoegde toegang tegen te gaan. Onze website maakt gebruik van SSL-encryptie.</p>
              </section>

              <section className="border-t border-white/10 pt-8 mt-12">
                <p>Heeft u vragen over dit privacybeleid? Neem contact op via ons contactformulier of WhatsApp: +31 6 84006668.</p>
              </section>
            </>
          ) : (
            <>
              <p className="text-zinc-400 text-sm">Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>

              <section>
                <h2 className="text-xl font-bold text-white mt-8 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>1. Who are we?</h2>
                <p>EG Solutions, located at Torenallee 101, 5617BR Eindhoven, is responsible for processing personal data as described in this privacy policy. Our director, Steven Franken, is the Data Protection Officer.</p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mt-8 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>2. What data do we collect?</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>First and last name</li>
                  <li>Email address</li>
                  <li>Phone number</li>
                  <li>Company name</li>
                  <li>Payment details (processed via Stripe, we do not store card data)</li>
                  <li>IP address and browser data (via cookies)</li>
                  <li>Data you provide via the contact form</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mt-8 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>3. Why do we collect this data?</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>To contact you for service delivery</li>
                  <li>To create quotations and invoices</li>
                  <li>To process payments</li>
                  <li>To inform you about changes to our services</li>
                  <li>To improve our website (analytics)</li>
                  <li>To comply with legal obligations</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mt-8 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>4. Retention Period</h2>
                <p>We do not retain personal data longer than strictly necessary. Fiscal data is retained for 7 years as required by law.</p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mt-8 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>5. Third Parties</h2>
                <p>We share data only when necessary for our agreement or legal obligations. We use Stripe (payments), Tawk.to (live chat), and PostHog (analytics).</p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mt-8 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>6. Your Rights</h2>
                <p>You have the right to access, correct, or delete your personal data. Submit requests via our contact form or WhatsApp (+31 6 84006668). We respond within 30 days. You may also file a complaint with the Dutch Data Protection Authority.</p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mt-8 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>7. Security</h2>
                <p>EG Solutions takes data protection seriously with appropriate measures against misuse, loss, and unauthorized access. Our website uses SSL encryption.</p>
              </section>

              <section className="border-t border-white/10 pt-8 mt-12">
                <p>Questions? Contact us via our contact form or WhatsApp: +31 6 84006668.</p>
              </section>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyBeleid;
